<template>
  <div :style="{ minWidth: '100%', overflowX: 'auto' }">
    <table :style="{ width: '100%', borderCollapse: 'collapse', fontFamily: 'inherit' }">
      <thead>
        <tr>
          <th v-for="c in columns" :key="c" :style="thStyle(c)" :aria-sort="ariaSort(c)" @click="emit('cycle-sort', c)">
            <span :style="{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexDirection: numeric(c) ? 'row-reverse' : 'row' }">
              {{ label(c) }}
              <span :style="{ display: 'inline-flex', alignItems: 'center', gap: '2px', opacity: ccFor(c) && ccFor(c).sort ? 1 : 0.35 }">
                <i
                  :class="`pi pi-${ccFor(c) && ccFor(c).sort ? (ccFor(c).sort === 'asc' ? 'sort-amount-up' : 'sort-amount-down') : 'sort'}`"
                  :style="{ fontSize: '10px' }"
                />
              </span>
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="r in sorted"
          :key="r[idKey]"
          class="les-trow"
          :style="{
            cursor: 'pointer',
            background: String(r[idKey]) === String(openId) ? 'var(--p-highlight-background)' : 'transparent',
            boxShadow: String(r[idKey]) === String(openId) ? 'inset 3px 0 0 var(--p-primary-500)' : 'none',
          }"
          @click="emit('open', r[idKey])"
          @dblclick="emit('open-expanded', r[idKey])"
        >
          <td v-for="c in columns" :key="c" :style="tdStyle(c)">
            <template v-if="c === '__primary'">{{ config.primary(r) }}</template>
            <FormatValue v-else :record="r" :field-key="c" :field-definitions="fieldDefinitions" :field-meta="fieldMeta" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
// Data-table view: sticky sortable headers + selectable rows. Ported from
// collection-frame TableView. Emits open / open-expanded (row id) and cycle-sort
// (column key).
import { computed } from 'vue'
import FormatValue from '@/admin-collection-layout/lib/formatValue.js'

const mono = 'var(--p-font-mono, ui-monospace, monospace)'

const props = defineProps({
  results: { type: Array, default: () => [] },
  config: { type: Object, required: true },
  fieldDefinitions: { type: Object, required: true },
  fieldMeta: { type: Object, default: () => ({}) },
  openId: { type: [String, Number], default: null },
  colConfig: { type: Array, default: () => [] },
})
const emit = defineEmits(['open', 'open-expanded', 'cycle-sort'])

const idKey = computed(() => props.config.idKey || 'id')
const fields = computed(() => props.fieldDefinitions.fields || {})
const columns = computed(() => props.colConfig.filter((c) => c.visible).map((c) => c.key))
const label = (c) => (c === '__primary' ? props.config.primaryLabel || props.config.kindLabel || 'Name' : (props.fieldMeta[c] || {}).label || c)
const numeric = (c) => c !== '__primary' && fields.value[c] && fields.value[c].type === 'number'

// The rows arrive in order — sorted by the shell, or by the server for a paged
// host — so the table draws them as they come rather than sorting a second time.
const sorted = computed(() => props.results)
const ccFor = (c) => props.colConfig.find((x) => x.key === c)
const ariaSort = (c) => ({ asc: 'ascending', desc: 'descending' })[ccFor(c)?.sort] ?? 'none'

const thStyle = (c) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1,
  textAlign: numeric(c) ? 'right' : 'left',
  padding: '9px 14px',
  background: 'var(--p-content-background, var(--p-content-background))',
  borderBottom: '1px solid var(--p-content-border-color)',
  fontSize: '11px',
  fontWeight: 800,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color: 'var(--p-text-muted-color)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  userSelect: 'none',
})
const tdStyle = (c) => ({
  padding: '10px 14px',
  borderBottom: '1px solid var(--p-content-border-color)',
  whiteSpace: 'nowrap',
  textAlign: numeric(c) ? 'right' : 'left',
  fontSize: '13px',
  fontWeight: c === '__primary' ? 700 : 400,
  color: 'var(--p-text-color)',
  fontFamily: numeric(c) ? mono : 'inherit',
})
</script>
